/**
 * calculatorBuilder.ts — typed client for /api/v1/admin/calculator-builder/*
 *
 * Wave 1 + Wave 2 endpoints.
 * Prices are NEVER written; read-only from Product.unitPrice on the server.
 * Pattern mirrors existing api/calculators/index.ts (axios `api` singleton,
 * withCredentials cookie auth, no manual Authorization header needed).
 */

import { api } from '../utils/api';

// ─── Shared types ─────────────────────────────────────────────────────────────

export type CalcKind = 'engine_backed' | 'no_code';
export type StockStatus = 'in_stock' | 'out_of_stock' | 'discontinued';

export interface CalcInputDescriptor {
  key: string;
  label: string;
  type: 'number' | 'select' | 'toggle';
  unit?: string;
  options?: string[];
}

export interface SizingRow {
  out: string;
  from: string;
}

export interface CatalogMappingRow {
  key: string;
  sku: string;
  productLabel: string;
  price?: number | null; // read-only, from Product.unitPrice
}

export interface FormulaRow {
  id?: string;
  outputKey: string;
  expr: string | Record<string, unknown>;
}

export interface RuleCondition {
  field: string;
  op: 'lte' | 'gte' | 'lt' | 'gt' | 'is';
  value: string;
}

export interface RuleRecommend {
  sku: string;
  productLabel?: string;
  qtyExpr: string | Record<string, unknown>;
}

export interface RecommendationRule {
  id?: string;
  priority: number;
  conditions: RuleCondition[] | Record<string, unknown>;
  recommends: RuleRecommend[];
}

// ─── Calculator Studio — CalcSpec mirror (matches backend config-eval/contract) ─
// FE-side structural mirror of the frozen backend CalcSpec. Expr/Cond kept loose
// (unknown) because the editors serialize them as JSON; the backend validates
// shape on save (isCalcSpec + price-free).

export type StudioExpr = Record<string, unknown>;
export type StudioCond = Record<string, unknown>;

export interface StudioInputField {
  key: string;
  label: string;
  type: 'number' | 'select' | 'toggle' | 'text';
  unit?: string;
  required?: boolean;
  options?: { value: string | number; label: string }[];
  group?: string;
  default?: unknown;
}

export interface SpecFormula {
  outputKey: string;
  expr: StudioExpr;
}

export interface SpecCriterion {
  key: string;
  weight: number;
  expr: StudioExpr;
  direction: 'higher' | 'lower';
  normalize: 'minmax' | 'target';
  target?: StudioExpr;
}

export interface SpecSlot {
  slot: 'primary' | 'alternative' | 'budget';
  rank: number;
  filter?: StudioCond;
}

export interface SpecRecommendation {
  productKey: string;
  filters: StudioCond[];
  criteria: SpecCriterion[];
  slots: SpecSlot[];
}

export interface CalcSpec {
  formulas: SpecFormula[];
  lookupRefs: string[];
  recommendation: SpecRecommendation;
  presentation: { html: string };
  proposalSection: { html: string };
  /** inputs schema lives on the config row, but the Studio also lets the admin
   *  design it; carried alongside the spec for the designer. */
  inputs?: StudioInputField[];
}

export interface LookupTable {
  tableKey: string;
  columns: string[];
  rows: (string | number)[][];
  mode?: 'exact' | 'nearest' | 'ceil';
}

export type LookupTableBody = LookupTable;

export interface GetSpecResponse {
  category: string;
  configId: string;
  configBacked: boolean;
  spec: CalcSpec;
  lookupTables: LookupTable[];
}

export interface StudioConfig {
  id: string;
  category: string;
  key: string;
  title: string;
  icon?: string | null;
  description?: string | null;
  configBacked: boolean;
  enabled: boolean;
  inputs: Record<string, unknown>;
  spec: CalcSpec;
}

export interface StudioConfigCreateBody {
  category: string;
  key: string;
  title: string;
  icon?: string;
  description?: string;
  inputs?: Record<string, unknown>;
  spec?: CalcSpec;
}

/** Per-candidate score breakdown returned by the config-eval recommender (S3 shape). */
export interface RecScoreTrace {
  criterionKey: string;
  raw: number;
  normalized: number;
  weight: number;
  contribution: number;
}

export interface RecCandidate {
  sku: string;
  slot?: 'primary' | 'alternative' | 'budget';
  score?: number;
  priceInr?: number | null;
  breakdown?: RecScoreTrace[];
}

export interface ConfigTestRunResponse {
  configBacked?: boolean;
  category?: string;
  outputs?: Record<string, number>;
  diagnostics?: { outputKey: string; value: number }[];
  recommendations?: RecCandidate[];
  pricingNote?: string;
}

// ─── GET /configs ─────────────────────────────────────────────────────────────

export interface CalcConfigSummary {
  category: string;
  title: string;
  kind: CalcKind;
  sizingReadOnly: boolean;
  productKey?: string;
  inputCount: number;
  inputs: CalcInputDescriptor[];
}

export interface ListConfigsResponse {
  configs: CalcConfigSummary[];
}

// ─── GET /configs/:category ───────────────────────────────────────────────────

export interface CalcConfigDetail extends CalcConfigSummary {
  sizing?: SizingRow[];
  catalogMapping: CatalogMappingRow[];
  pricingNote?: string;
  // no-code config (if present):
  noCodeConfig?: NoCodeConfig | null;
}

// ─── POST /configs/:category/test-run ────────────────────────────────────────

export interface TestRunRecommendation {
  sku: string;
  qty: number;
  price: number | null;
}

export interface TestRunResponse {
  sizing?: Record<string, unknown>;
  recommendations: TestRunRecommendation[];
  noCodePath?: boolean;
  configId?: string;
  outputs?: Record<string, unknown>;
  pricingNote?: string;
  _raw?: unknown;
}

// ─── Catalog editing ──────────────────────────────────────────────────────────

export interface CatalogRowCreateBody {
  sku: string;
  family?: string;
  ratedKw?: number;
  ratedCop?: number;
  tankLitres?: number;
  stockStatus?: StockStatus;
  attributes?: Record<string, unknown>;
  effectiveFrom?: string;
  effectiveUntil?: string;
}

export interface CatalogRowPatchBody {
  family?: string;
  ratedKw?: number;
  ratedCop?: number;
  tankLitres?: number;
  stockStatus?: StockStatus;
  attributes?: Record<string, unknown>;
  effectiveUntil?: string;
}

// ─── No-code config ───────────────────────────────────────────────────────────

export interface NoCodeConfig {
  id: string;
  category: string;
  enabled: boolean;
  inputs: Record<string, unknown>;
  formulas?: FormulaRow[];
  recommendationRules?: RecommendationRule[];
}

export interface NoCodeConfigCreateBody {
  category: string;
  inputs: Record<string, unknown>;
  enabled?: boolean;
}

export interface NoCodeConfigPatchBody {
  inputs?: Record<string, unknown>;
  enabled?: boolean;
}

// ─── Formula CRUD ─────────────────────────────────────────────────────────────

export interface FormulaCreateBody {
  outputKey: string;
  expr: Record<string, unknown>;
}

export interface FormulaPatchBody {
  outputKey?: string;
  expr?: Record<string, unknown>;
}

// ─── Rule CRUD ────────────────────────────────────────────────────────────────

export interface RuleCreateBody {
  priority: number;
  conditions: Record<string, unknown>;
  recommends: Array<{
    sku: string;
    qtyExpr: Record<string, unknown>;
    condition?: Record<string, unknown>;
  }>;
}

export interface RulePatchBody {
  priority?: number;
  conditions?: Record<string, unknown>;
  recommends?: Array<{
    sku: string;
    qtyExpr: Record<string, unknown>;
    condition?: Record<string, unknown>;
  }>;
}

// ─── No-code test-run ─────────────────────────────────────────────────────────

export interface NoCodeTestRunResponse {
  noCodePath: true;
  configId: string;
  outputs: Record<string, unknown>;
  recommendations: TestRunRecommendation[];
  pricingNote: string;
}

// ─── API client ───────────────────────────────────────────────────────────────

const BASE = '/admin/calculator-builder';

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Record<string, unknown>)['data'] as T;
  }
  return payload as T;
}

export const calculatorBuilderApi = {
  // ── Wave 1 ──────────────────────────────────────────────────────────────────

  async listConfigs(): Promise<ListConfigsResponse> {
    const res = await api.get(`${BASE}/configs`);
    return res.data as ListConfigsResponse;
  },

  async getConfig(category: string): Promise<CalcConfigDetail> {
    const res = await api.get(`${BASE}/configs/${encodeURIComponent(category)}`);
    return res.data as CalcConfigDetail;
  },

  async testRun(category: string, inputs: Record<string, unknown>): Promise<TestRunResponse> {
    const res = await api.post(`${BASE}/configs/${encodeURIComponent(category)}/test-run`, inputs);
    return res.data as TestRunResponse;
  },

  // ── Wave 2 — catalog editing ─────────────────────────────────────────────────

  async addCatalogRow(productKey: string, body: CatalogRowCreateBody) {
    const res = await api.post(`${BASE}/catalog/${encodeURIComponent(productKey)}`, body);
    return unwrap(res.data);
  },

  async patchCatalogRow(productKey: string, sku: string, body: CatalogRowPatchBody) {
    const res = await api.patch(
      `${BASE}/catalog/${encodeURIComponent(productKey)}/${encodeURIComponent(sku)}`,
      body,
    );
    return unwrap(res.data);
  },

  async deleteCatalogRow(productKey: string, sku: string) {
    const res = await api.delete(
      `${BASE}/catalog/${encodeURIComponent(productKey)}/${encodeURIComponent(sku)}`,
    );
    return unwrap(res.data);
  },

  // ── Wave 2 — no-code configs ─────────────────────────────────────────────────

  async listNoCodeConfigs(): Promise<NoCodeConfig[]> {
    const res = await api.get(`${BASE}/no-code-configs`);
    const data = res.data;
    return Array.isArray(data) ? data : (data?.configs ?? data?.data ?? []);
  },

  async createNoCodeConfig(body: NoCodeConfigCreateBody): Promise<NoCodeConfig> {
    const res = await api.post(`${BASE}/no-code-configs`, body);
    return unwrap<NoCodeConfig>(res.data);
  },

  async patchNoCodeConfig(id: string, body: NoCodeConfigPatchBody) {
    const res = await api.patch(`${BASE}/no-code-configs/${encodeURIComponent(id)}`, body);
    return unwrap(res.data);
  },

  // ── Wave 2 — formulas ────────────────────────────────────────────────────────

  async addFormula(configId: string, body: FormulaCreateBody) {
    const res = await api.post(`${BASE}/no-code-configs/${encodeURIComponent(configId)}/formulas`, body);
    return unwrap(res.data);
  },

  async patchFormula(configId: string, fid: string, body: FormulaPatchBody) {
    const res = await api.patch(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/formulas/${encodeURIComponent(fid)}`,
      body,
    );
    return unwrap(res.data);
  },

  async deleteFormula(configId: string, fid: string) {
    const res = await api.delete(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/formulas/${encodeURIComponent(fid)}`,
    );
    return unwrap(res.data);
  },

  // ── Wave 2 — recommendation rules ────────────────────────────────────────────

  async addRule(configId: string, body: RuleCreateBody) {
    const res = await api.post(`${BASE}/no-code-configs/${encodeURIComponent(configId)}/rules`, body);
    return unwrap(res.data);
  },

  async patchRule(configId: string, rid: string, body: RulePatchBody) {
    const res = await api.patch(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/rules/${encodeURIComponent(rid)}`,
      body,
    );
    return unwrap(res.data);
  },

  async deleteRule(configId: string, rid: string) {
    const res = await api.delete(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/rules/${encodeURIComponent(rid)}`,
    );
    return unwrap(res.data);
  },

  // ── Wave 2 — no-code test-run ────────────────────────────────────────────────

  async testRunNoCode(configId: string, inputs: Record<string, unknown>): Promise<NoCodeTestRunResponse> {
    const res = await api.post(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/test-run`,
      inputs,
    );
    return res.data as NoCodeTestRunResponse;
  },

  // ── Wave 2 — revert ──────────────────────────────────────────────────────────

  async revert(configId: string) {
    const res = await api.post(
      `${BASE}/no-code-configs/${encodeURIComponent(configId)}/revert`,
    );
    return unwrap(res.data);
  },

  // ══ Calculator Studio (S5) — spec / lookup / create-config / category revert ══

  async getSpec(category: string): Promise<GetSpecResponse> {
    const res = await api.get(`${BASE}/configs/${encodeURIComponent(category)}/spec`);
    return res.data as GetSpecResponse;
  },

  async putSpec(category: string, spec: CalcSpec): Promise<{ config: { id: string; category: string; spec: CalcSpec } }> {
    const res = await api.put(`${BASE}/configs/${encodeURIComponent(category)}/spec`, { spec });
    return res.data as { config: { id: string; category: string; spec: CalcSpec } };
  },

  async createConfig(body: StudioConfigCreateBody): Promise<{ config: StudioConfig }> {
    const res = await api.post(`${BASE}/configs`, body);
    return res.data as { config: StudioConfig };
  },

  async addLookupTable(category: string, body: LookupTableBody): Promise<{ table: LookupTable }> {
    const res = await api.post(`${BASE}/configs/${encodeURIComponent(category)}/lookup-tables`, body);
    return res.data as { table: LookupTable };
  },

  async putLookupTable(category: string, tableKey: string, body: Omit<LookupTableBody, 'tableKey'>): Promise<{ table: LookupTable }> {
    const res = await api.put(
      `${BASE}/configs/${encodeURIComponent(category)}/lookup-tables/${encodeURIComponent(tableKey)}`,
      body,
    );
    return res.data as { table: LookupTable };
  },

  async deleteLookupTable(category: string, tableKey: string): Promise<{ deleted: boolean; tableKey: string }> {
    const res = await api.delete(
      `${BASE}/configs/${encodeURIComponent(category)}/lookup-tables/${encodeURIComponent(tableKey)}`,
    );
    return res.data as { deleted: boolean; tableKey: string };
  },

  /** Test-run a config-backed calc (evalConfig path). Returns outputs + recs + score trace. */
  async testRunConfig(category: string, inputs: Record<string, unknown>): Promise<ConfigTestRunResponse> {
    const res = await api.post(`${BASE}/configs/${encodeURIComponent(category)}/test-run`, inputs);
    return res.data as ConfigTestRunResponse;
  },

  async revertCategory(category: string): Promise<{ reverted: boolean; restoredFromSnapshotId: string }> {
    const res = await api.post(`${BASE}/configs/${encodeURIComponent(category)}/revert`);
    return res.data as { reverted: boolean; restoredFromSnapshotId: string };
  },
};
